import {z} from 'zod';

import {type Nullish} from 'shared/web';

import {optionalText, t} from './catalogueUi';
import {grossFromNet, trimPriceDecimals} from './netFromGross';

const MAX_MONEY_WHOLE_DIGITS = 13;
const MAX_MONEY_DECIMALS = 2;
const MAX_PRICE_DECIMALS = 4;
const isDigits = (value: string) =>
  value.length > 0 && [...value].every((character) => character >= '0' && character <= '9');
const isDecimalInput = (value: string, maxDecimals: number) => {
  const parts = value.replace(',', '.').split('.');
  return (
    parts.length <= 2 &&
    parts[0].length <= MAX_MONEY_WHOLE_DIGITS &&
    isDigits(parts[0]) &&
    (parts.length === 1 || (parts[1].length <= maxDecimals && isDigits(parts[1])))
  );
};
const normalizeMoneyInput = (value: string) => {
  const [whole, fraction = ''] = value.replace(',', '.').split('.');
  return trimPriceDecimals(`${whole}.${fraction.padEnd(MAX_MONEY_DECIMALS, '0')}`);
};
const invalidAmount = () => ({
  message: t(
    'entity.newVehicle.catalogue.validation.invalidAmount',
    'Enter an amount of 0 or more with up to 2 decimal places.'
  ),
});
const invalidPrice = () => ({
  message: t(
    'entity.newVehicle.catalogue.validation.invalidPrice',
    'Enter an amount of 0 or more with up to 4 decimal places.'
  ),
});
export const moneyInput = z
  .string()
  .refine((value) => isDecimalInput(value, MAX_MONEY_DECIMALS), invalidAmount)
  .transform(normalizeMoneyInput);
export const optionalMoneyInput = optionalText
  .refine((value) => value === '' || isDecimalInput(value, MAX_MONEY_DECIMALS), invalidAmount)
  .transform((value) => value && normalizeMoneyInput(value));
export const priceInput = z
  .string()
  .refine((value) => isDecimalInput(value, MAX_PRICE_DECIMALS), invalidPrice)
  .transform(normalizeMoneyInput);
export const optionalPriceInput = optionalText
  .refine((value) => value === '' || isDecimalInput(value, MAX_PRICE_DECIMALS), invalidPrice)
  .transform((value) => value && normalizeMoneyInput(value));

export const grossOfNet = (net: string, vatRate: string) => {
  const parsedNet = priceInput.safeParse(net);
  return parsedNet.success ? grossFromNet(parsedNet.data, vatRate) : undefined;
};

export const negativeMargin = (price: string | Nullish, cost: string | Nullish) => {
  const parsedPrice = priceInput.safeParse(price ?? '');
  const parsedCost = moneyInput.safeParse(cost ?? '');
  if (!parsedPrice.success || !parsedCost.success) {
    return undefined;
  }
  const margin = Number(parsedPrice.data) - Number(parsedCost.data);
  return margin < 0 ? margin : undefined;
};
